import { useEffect } from 'react';

import { type Board } from '@air/api/types';

import { useAppliedCustomFieldsFilters } from '~/hooks/filters/useAppliedCustomFieldsFilters';
import { useAppliedTagsFilters } from '~/hooks/filters/useAppliedTagsFilters';
import { useSetFiltersFromContextToRedux } from '~/hooks/filters/useSetFiltersFromContextToRedux';
import { useFiltersModifiedManuallyContext } from '~/providers/FiltersModifiedManuallyProvider';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { useGetPublicFilterOptions } from '~/swr-hooks/filters/useGetPublicFilterOptions';
import { useGetPublicFiltersEntities } from '~/swr-hooks/filters/useGetPublicFiltersEntities';
import { customFieldUrlParamNeedsCatalog } from '~/utils/filters/plainTextCustomFieldFilter';
import { catalogCustomFieldsForHydration, customFieldCatalogHydration } from '~/utils/filters/urlParamsToFiltersUtils';

export const useSetPublicFiltersFromContextToRedux = ({ boardId }: { boardId?: Board['id'] }) => {
  const { getFiltersModifiedManually, setFiltersModifiedManually } = useFiltersModifiedManuallyContext();
  const {
    customFields: catalogCustomFields,
    error: catalogError,
    isReady: catalogReady,
  } = usePublicCustomFieldsContext();
  const {
    filters: { customFields: customFieldParams },
  } = useFiltersContext();
  const needsCustomFieldCatalog = Object.keys(customFieldParams ?? {}).some(customFieldUrlParamNeedsCatalog);
  const catalogHydration = customFieldCatalogHydration({
    customFields: catalogCustomFields,
    isReady: catalogReady,
    error: catalogError,
  });
  const catalogForHydration = catalogCustomFieldsForHydration(catalogHydration);
  const catalogSettled = catalogHydration.status !== 'pending';

  const { appliedTagsFilters } = useAppliedTagsFilters();
  const { appliedCustomFieldsFilters } = useAppliedCustomFieldsFilters();

  const { data: options } = useGetPublicFilterOptions({ boardId });
  const { data: entities } = useGetPublicFiltersEntities({
    tagLabels: appliedTagsFilters,
    customFields: appliedCustomFieldsFilters,
  });

  const { setFiltersFromContextToRedux } = useSetFiltersFromContextToRedux({
    options,
    entities,
    catalogCustomFields: catalogForHydration,
    preserveUnresolvedPlainText: catalogHydration.status === 'failed',
  });

  useEffect(() => {
    if (options && entities && (!needsCustomFieldCatalog || catalogSettled) && getFiltersModifiedManually()) {
      if (!needsCustomFieldCatalog || catalogHydration.status === 'ready') {
        setFiltersModifiedManually(false);
      }
      setFiltersFromContextToRedux();
    }
  }, [
    options,
    entities,
    needsCustomFieldCatalog,
    catalogSettled,
    catalogHydration.status,
    setFiltersFromContextToRedux,
    getFiltersModifiedManually,
    setFiltersModifiedManually,
  ]);

  return {
    setFiltersFromContextToRedux,
  };
};
